import { useEffect, useMemo, useRef, useState } from 'react';

export type ExtRes = { name: string; value: string };
export type ProjectDto = {
  id: number;
  defaultBudgetId?: number | null;
  externalResources?: ExtRes[];
};

export function getRedmineIdentifierFromProject(p: { externalResources?: ExtRes[] }): string {
  const list = p?.externalResources ?? [];

  const redmine = list.find(r => {
    const name = (r?.name ?? '').trim().toLowerCase();
    return name === 'redmine' || name === 'redmineweburl';
  });
  if (!redmine?.value) throw new Error('Redmine project is not set');

  try {
    const url = new URL(redmine.value);
    const parts = url.pathname.split('/').filter(Boolean);
    return parts[parts.length - 1];
  } catch {
    throw new Error('Invalid Redmine URL format');
  }
}

export function useProjectBootstrap(opts: {
  id?: string;
  contextProject?: ProjectDto;
  fetchProjectById: (id: string) => Promise<ProjectDto>;
}) {
  const { id, contextProject, fetchProjectById } = opts;
  const [project, setProject] = useState<ProjectDto | undefined>(contextProject);
  const fetchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (project || contextProject || !id || fetchedRef.current) return;
      fetchedRef.current = true;
      try {
        const p = await fetchProjectById(id);
        if (!cancelled) setProject(p);
      } catch {
        if (!cancelled) setProject(undefined);
      }
    };

    if (!project && contextProject) setProject(contextProject);

    run().catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, contextProject, project]);

  return { project, setProject };
}

export function useRedmineIdentifier(project?: ProjectDto) {
  return useMemo<string | undefined>(() => {
    if (!project) return undefined;
    try {
      return getRedmineIdentifierFromProject(project);
    } catch {
      return undefined;
    }
  }, [project]);
}
